import { configurarMiraScoreSchema } from '@locaveri/shared'
import { KeyRound, LoaderCircle, PlugZap, Trash2 } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  useConfigurarMiraScore,
  useIntegracaoMiraScore,
  useRemoverMiraScore,
  useTestarMiraScore,
} from '@/features/analise-cadastral/api'
import { errorMessage } from '@/lib/api'
import { formatarDataHora } from '@/lib/datas'
import { usePermissao } from '@/lib/permissoes'

/**
 * CONFIGURAÇÃO DO MIRA SCORE — a chave da conta da locadora no Mira.
 *
 * ===========================================================================
 * A CHAVE ENTRA E NÃO SAI
 * ===========================================================================
 * A tela nunca mostra a chave gravada — só os 4 últimos caracteres, para a
 * pessoa saber QUAL está lá. A API confere a chave no Mira antes de gravar: a
 * chave errada é recusada aqui, com o gestor na frente da tela, e não na
 * primeira reserva da fila. Ver `docs/integracao-mira-score.md`.
 */
export function MiraScorePage(): ReactElement {
  const integracao = useIntegracaoMiraScore()
  const configurar = useConfigurarMiraScore()
  const testar = useTestarMiraScore()
  const remover = useRemoverMiraScore()
  const podeEditar = usePermissao('credit_analysis.update')

  const [chave, setChave] = useState('')
  const [erroDoCampo, setErroDoCampo] = useState<string | null>(null)

  if (integracao.isPending) {
    return <LoadingState rotulo="Carregando a integração…" />
  }
  if (integracao.isError) {
    return (
      <ErrorState
        titulo="Não foi possível carregar a integração"
        erro={integracao.error}
        aoTentarNovamente={() => {
          void integracao.refetch()
        }}
      />
    )
  }

  const dados = integracao.data

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Mira Score</h1>
        <p className="text-sm text-fg-secondary">
          Análise cadastral do cliente antes de aprovar a reserva: CPF, nota de crédito (em faixa:
          alta, média ou baixa), CNH, processos e selfie com prova de vida. Clientes sem locação
          concluída são analisados sozinhos ao entrar na fila; a análise vale 90 dias. Cada consulta
          é cobrada da locadora pelo Mira.
        </p>
      </div>

      {dados.modo === 'DESLIGADO' ? (
        <Alert>
          <AlertTitle>Integração desligada nesta instalação</AlertTitle>
          <AlertDescription>
            O Mira Score não está ligado no servidor. Fale com o suporte da plataforma para ligar.
          </AlertDescription>
        </Alert>
      ) : null}

      {dados.modo === 'FAKE' ? (
        <Alert>
          <AlertTitle>Modo simulado</AlertTitle>
          <AlertDescription>
            Este ambiente usa um Mira Score SIMULADO: nada é consultado nem cobrado, e qualquer
            chave é aceita. Os CPFs de teste do Mira têm resultado fixo — 234.567.891-73 favorável,
            654.321.987-46 atenção (nota baixa), 345.678.912-28 desfavorável (CNH suspensa),
            456.789.123-64 falha (fontes fora do ar), 369.147.258-37 atenção (processos
            indisponíveis) e 258.147.369-09 atenção (sem nota de crédito). Outros CPFs saem pelo
            último dígito (0 a 5 favorável, 6 e 7 atenção, 8 e 9 desfavorável).
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-4" /> Chave da locadora
          </CardTitle>
          <CardDescription>
            Gerada no Mira Score para a conta desta locadora. Ela fica guardada cifrada e não volta
            para a tela.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {dados.configurada ? (
              <>
                <Badge variant="success">Configurada</Badge>
                <span className="text-fg-secondary">
                  termina em <span className="font-mono">…{dados.finalDaChave}</span>
                  {dados.atualizadaEm === null
                    ? ''
                    : ` · atualizada em ${formatarDataHora(dados.atualizadaEm)}`}
                </span>
              </>
            ) : (
              <Badge variant="warning">Ainda não configurada</Badge>
            )}
            {dados.ultimoTesteEm === null ? null : (
              <Badge variant={dados.ultimoTesteOk === true ? 'success' : 'danger'}>
                Último teste {dados.ultimoTesteOk === true ? 'ok' : 'recusado'} em{' '}
                {formatarDataHora(dados.ultimoTesteEm)}
              </Badge>
            )}
          </div>

          {podeEditar && dados.modo !== 'DESLIGADO' ? (
            <form
              className="space-y-3"
              onSubmit={(evento) => {
                evento.preventDefault()
                const lido = configurarMiraScoreSchema.safeParse({ chave })
                if (!lido.success) {
                  setErroDoCampo(lido.error.issues[0]?.message ?? 'Chave inválida.')
                  return
                }
                setErroDoCampo(null)
                configurar.mutate(lido.data, {
                  onSuccess: () => {
                    setChave('')
                    toast.success('Chave conferida no Mira Score e gravada.')
                  },
                  onError: (erro) => {
                    setErroDoCampo(errorMessage(erro))
                  },
                })
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="chave-mira">
                  {dados.configurada ? 'Trocar a chave' : 'Colar a chave'}
                </Label>
                <Input
                  id="chave-mira"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={chave}
                  onChange={(evento) => {
                    setChave(evento.target.value)
                  }}
                  aria-invalid={erroDoCampo !== null}
                  aria-describedby={erroDoCampo === null ? undefined : 'chave-mira-erro'}
                />
                {erroDoCampo === null ? null : (
                  <p id="chave-mira-erro" className="text-xs text-danger-fg">
                    {erroDoCampo}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={configurar.isPending || chave.trim() === ''}>
                  {configurar.isPending ? <LoaderCircle className="animate-spin" /> : <KeyRound />}
                  Conferir e salvar
                </Button>
                {dados.configurada ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={testar.isPending}
                      onClick={() => {
                        testar.mutate(undefined, {
                          onSuccess: () => {
                            toast.success('O Mira Score aceitou a chave.')
                          },
                          onError: (erro) => {
                            toast.error(errorMessage(erro))
                          },
                        })
                      }}
                    >
                      {testar.isPending ? <LoaderCircle className="animate-spin" /> : <PlugZap />}
                      Testar conexão
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={remover.isPending}
                      onClick={() => {
                        if (
                          !window.confirm(
                            'Remover a chave desliga a análise automática desta locadora. As análises já feitas continuam. Remover?',
                          )
                        ) {
                          return
                        }
                        remover.mutate(undefined, {
                          onSuccess: () => {
                            toast.success('Chave removida. A análise automática está desligada.')
                          },
                          onError: (erro) => {
                            toast.error(errorMessage(erro))
                          },
                        })
                      }}
                    >
                      <Trash2 /> Remover a chave
                    </Button>
                  </>
                ) : null}
              </div>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
